import {
  type ReportOrganizingTableFormat4Model,
  type ReportOrganizingTableFormat4SummaryDataModel,
  format4ColumnType,
} from '@/features/mold/models/organizing-table';
import { t } from '@/i18n';
import {
  addPrefixToStringMapValues,
  createCsv,
  formatAmountReport,
} from '@/utils';
import type { z } from 'zod';

type ReportDetailsType = z.infer<typeof ReportOrganizingTableFormat4Model>;

export const format4HeaderModifier = ({
  currentYear,
}: {
  currentYear: number;
}) => {
  const previousYearProjectCountHeaderName = t(
    'report-organizing-table-format4-header-previous-year-project-count',
    { year: currentYear - 1 },
  );
  const abolitionProjectCountHeaderName = `${t('report-organizing-table-format4-header-abolition')}${t('project-count')}`;
  const abolitionProjectReflectedAmountHeaderName = `${t('report-organizing-table-format4-header-abolition')}${t('reflected-amount')}`;
  const reductionProjectCountHeaderName = `${t('report-organizing-table-format4-header-reduction')}${t('project-count')}`;
  const reductionProjectReflectedAmountHeaderName = `${t('report-organizing-table-format4-header-reduction')}${t('reflected-amount')}`;
  const abolitionReductionTotalProjectCountHeaderName = `${t('report-organizing-table-format4-header-abolition-reduction-total')}${t('project-count')}`;
  const abolitionReductionTotalProjectReflectedAmountHeaderName = `${t('report-organizing-table-format4-header-abolition-reduction-total')}${t('reflected-amount')}`;
  const improvementProjectCountHeaderName = t(
    'report-organizing-table-format4-header-improvement-project-count',
  );
  const projectRequestedAmountHeaderName = t(
    'report-organizing-table-format4-header-project-requested-amount',
    { year: currentYear + 1 },
  );

  const generalSpecialAccount = `${t('general-account')}＋${t('special-account')}`;
  const generalAccount = t('general-account');
  const specialAccount = t('special-account');

  const generalSpecialAccountSummary = {
    generalSpecialPreviousYearProjectCount: previousYearProjectCountHeaderName,
    generalSpecialAbolitionProjectCount: abolitionProjectCountHeaderName,
    generalSpecialAbolitionProjectReflectedAmount:
      abolitionProjectReflectedAmountHeaderName,
    generalSpecialReductionProjectCount: reductionProjectCountHeaderName,
    generalSpecialReductionProjectReflectedAmount:
      reductionProjectReflectedAmountHeaderName,
    generalSpecialImprovementProjectCount: improvementProjectCountHeaderName,
  } as const;

  const generalAccountSummary = {
    generalPreviousYearProjectCount: previousYearProjectCountHeaderName,
    generalAbolitionProjectCount: abolitionProjectCountHeaderName,
    generalAbolitionProjectReflectedAmount:
      abolitionProjectReflectedAmountHeaderName,
    generalReductionProjectCount: reductionProjectCountHeaderName,
    generalReductionProjectReflectedAmount:
      reductionProjectReflectedAmountHeaderName,
    generalAbolitionReductionTotalProjectCount:
      abolitionReductionTotalProjectCountHeaderName,
    generalAbolitionReductionTotalProjectReflectedAmount:
      abolitionReductionTotalProjectReflectedAmountHeaderName,
    generalImprovementProjectCount: improvementProjectCountHeaderName,
    generalProjectRequestedAmount: projectRequestedAmountHeaderName,
  } as const;

  const specialAccountSummary = {
    specialPreviousYearProjectCount: previousYearProjectCountHeaderName,
    specialAbolitionProjectCount: abolitionProjectCountHeaderName,
    specialAbolitionProjectReflectedAmount:
      abolitionProjectReflectedAmountHeaderName,
    specialReductionProjectCount: reductionProjectCountHeaderName,
    specialReductionProjectReflectedAmount:
      reductionProjectReflectedAmountHeaderName,
    specialAbolitionReductionTotalProjectCount:
      abolitionReductionTotalProjectCountHeaderName,
    specialAbolitionReductionTotalProjectReflectedAmount:
      abolitionReductionTotalProjectReflectedAmountHeaderName,
    specialImprovementProjectCount: improvementProjectCountHeaderName,
    specialProjectRequestedAmount: projectRequestedAmountHeaderName,
  } as const;

  return {
    jurisdiction: t('jurisdiction'),
    previousYearProjectCountHeaderName,
    generalSpecialAccount,
    generalAccount,
    specialAccount,
    improvementProjectCountHeaderName,
    abolition: t('report-organizing-table-format4-header-abolition'),
    reduction: t('report-organizing-table-format4-header-reduction'),
    reflectedAmount: t('reflected-amount'),
    projectCount: t('project-count'),
    abolitionReductionTotal: t(
      'report-organizing-table-format4-header-abolition-reduction-total',
    ),
    projectRequestedAmountHeaderName,
    ...addPrefixToStringMapValues(
      generalSpecialAccountSummary,
      generalSpecialAccount,
    ),
    ...addPrefixToStringMapValues(generalAccountSummary, generalAccount),
    ...addPrefixToStringMapValues(specialAccountSummary, specialAccount),
  };
};

export const getFormat4CsvData = ({
  year,
  reports,
}: {
  year: number;
  reports: ReportDetailsType;
}) => {
  return createCsv({
    headerKeys: format4ColumnType.options,
    headerConverter: format4HeaderModifier({ currentYear: year }),
    body: getFormattedReports({ reports }),
  });
};

type DataType = Omit<
  z.infer<typeof ReportOrganizingTableFormat4SummaryDataModel>,
  | 'abolitionReductionProjectCount'
  | 'abolitionReductionProjectReflectedAmount'
  | 'projectRequestedAmount'
> & {
  abolitionReductionProjectReflectedAmount?: number | null;
  projectRequestedAmount?: number | null;
};

export const format4ValueConverter = {
  abolitionProjectReflectedAmount: ({
    data,
    type,
  }: { data: DataType; type: 'csv' | 'pdf' }) =>
    formatAmountReport(data.abolitionProjectReflectedAmount, type),
  reductionProjectReflectedAmount: ({
    data,
    type,
  }: { data: DataType; type: 'csv' | 'pdf' }) =>
    formatAmountReport(data.reductionProjectReflectedAmount, type),
  abolitionReductionProjectReflectedAmount: ({
    data,
    type,
  }: { data: DataType; type: 'csv' | 'pdf' }) =>
    formatAmountReport(data.abolitionReductionProjectReflectedAmount, type),
  projectRequestedAmount: ({
    data,
    type,
  }: { data: DataType; type: 'csv' | 'pdf' }) =>
    formatAmountReport(data.projectRequestedAmount, type),
};

const getFormattedReports = ({ reports }: { reports: ReportDetailsType }) => {
  return reports.organizations.map((organization) => {
    const generalSpecialAccountSummary =
      organization.summaryByAccount.generalSpecial;
    const generalAccountSummary = organization.summaryByAccount.general;
    const specialAccountSummary = organization.summaryByAccount.special;

    return {
      jurisdiction: organization.name,
      generalSpecialPreviousYearProjectCount:
        generalSpecialAccountSummary.previousYearProjectCount,
      generalSpecialAbolitionProjectCount:
        generalSpecialAccountSummary.abolitionProjectCount,
      generalSpecialAbolitionProjectReflectedAmount:
        format4ValueConverter.abolitionProjectReflectedAmount({
          data: generalSpecialAccountSummary,
          type: 'csv',
        }),
      generalSpecialReductionProjectCount:
        generalSpecialAccountSummary.reductionProjectCount,
      generalSpecialReductionProjectReflectedAmount:
        format4ValueConverter.reductionProjectReflectedAmount({
          data: generalSpecialAccountSummary,
          type: 'csv',
        }),
      generalSpecialImprovementProjectCount:
        generalSpecialAccountSummary.improvementProjectCount,
      generalPreviousYearProjectCount:
        generalAccountSummary.previousYearProjectCount,
      generalAbolitionProjectCount: generalAccountSummary.abolitionProjectCount,
      generalAbolitionProjectReflectedAmount:
        format4ValueConverter.abolitionProjectReflectedAmount({
          data: generalAccountSummary,
          type: 'csv',
        }),
      generalReductionProjectCount: generalAccountSummary.reductionProjectCount,
      generalReductionProjectReflectedAmount:
        format4ValueConverter.reductionProjectReflectedAmount({
          data: generalAccountSummary,
          type: 'csv',
        }),
      generalAbolitionReductionTotalProjectCount:
        generalAccountSummary.abolitionReductionProjectCount,
      generalAbolitionReductionTotalProjectReflectedAmount:
        format4ValueConverter.abolitionReductionProjectReflectedAmount({
          data: generalAccountSummary,
          type: 'csv',
        }),
      generalImprovementProjectCount:
        generalAccountSummary.improvementProjectCount,
      generalProjectRequestedAmount:
        format4ValueConverter.projectRequestedAmount({
          data: generalAccountSummary,
          type: 'csv',
        }),
      specialPreviousYearProjectCount:
        specialAccountSummary.previousYearProjectCount,
      specialAbolitionProjectCount: specialAccountSummary.abolitionProjectCount,
      specialAbolitionProjectReflectedAmount:
        format4ValueConverter.abolitionProjectReflectedAmount({
          data: specialAccountSummary,
          type: 'csv',
        }),
      specialReductionProjectCount: specialAccountSummary.reductionProjectCount,
      specialReductionProjectReflectedAmount:
        format4ValueConverter.reductionProjectReflectedAmount({
          data: specialAccountSummary,
          type: 'csv',
        }),
      specialAbolitionReductionTotalProjectCount:
        specialAccountSummary.abolitionReductionProjectCount,
      specialAbolitionReductionTotalProjectReflectedAmount:
        format4ValueConverter.abolitionReductionProjectReflectedAmount({
          data: specialAccountSummary,
          type: 'csv',
        }),
      specialImprovementProjectCount:
        specialAccountSummary.improvementProjectCount,
      specialProjectRequestedAmount:
        format4ValueConverter.projectRequestedAmount({
          data: specialAccountSummary,
          type: 'csv',
        }),
    };
  });
};
